import React, { useEffect, useState } from 'react'
import '../website/styles/main.scss'
import { ArrowLeft, ArrowRight } from '@phosphor-icons/react'
import { partnerContentStore } from 'state/setting/partnerContent'
import PartnersEditor from './components/PartnersEditor'
import PartnersContentPreview from './components/PartnersContentPreview'
import {
  useDeletePartnerContent,
  useGetPartnerContent,
} from 'api/Settings/PartnerContent'
import ButtonSpinner from 'components/global/ButtonSpinner'
import Spinner from 'components/Spinner'
import ReorderModal from './Modals/ReorderModal'

const Partners: React.FC = () => {
  const { slides, currentSlide, setCurrentSlide, removeSlide } =
    partnerContentStore()

  const { setSlides, reorderSlidesModal, setReorderSlidesModal } =
    partnerContentStore(store => ({
      setSlides: store.setSlides,
      reorderSlidesModal: store.reorderSlidesModal,
      setReorderSlidesModal: store.setReorderSlidesModal,
    }))

  const { data, isLoading, isFetching, refetch } = useGetPartnerContent()
  useEffect(() => {
    refetch()
  }, [refetch])

  useEffect(() => {
    if (data && Array.isArray(data)) {
      const mappedSlides = data.map((item: any, idx: number) => {
        // Derive cover photo IDs and preview URL from API response
        const coverPhotosArray = Array.isArray(item.coverPhotos)
          ? item.coverPhotos
          : []

        const firstCover =
          coverPhotosArray.length > 0 ? coverPhotosArray[0] : undefined

        const coverPhotosIds =
          (Array.isArray(item.coverPhotosIds) && item.coverPhotosIds.length > 0
            ? item.coverPhotosIds
            : coverPhotosArray
                .map((p: any) => p?._id || p?.id)
                .filter(Boolean)) || []

        const coverPhotoPreviewUrl =
          firstCover?.thumb ||
          firstCover?.post ||
          firstCover?.original ||
          item.coverPhotoPreviewUrl ||
          ''

        return {
          id: item.id || item._id || `local-${Date.now()}-${idx}`,
          title: item.title || '',
          content: item.content || '',
          coverPhotosIds,
          coverPhotoPreviewUrl,
          buttons:
            item.buttons && item.buttons.length > 0
              ? item.buttons
              : [{ text: '', link: '' }],
          layout: item.layout || 'left',
        }
      })
      setSlides(mappedSlides)
    }
  }, [data, setSlides])

  const [clickedId, setClickedId] = useState<string | number | undefined>('')
  const { mutate: deletePartnerContentMutation, isLoading: deleteLoading } =
    useDeletePartnerContent()

  const handleDeleteSlide = (slideId: string) => {
    if (slideId.includes('local-')) {
      removeSlide(slideId)
      const newSlidesLength = partnerContentStore.getState().slides.length

      if (newSlidesLength === 0) {
        setCurrentSlide(0)
      } else if (currentSlide >= newSlidesLength) {
        setCurrentSlide(newSlidesLength - 1)
      }
    } else {
      setClickedId(slideId)
      deletePartnerContentMutation(slideId as string, {
        onSuccess: () => {
          removeSlide(slideId)
          setClickedId(undefined)
          const newSlidesLength = partnerContentStore.getState().slides.length

          if (newSlidesLength === 0) {
            setCurrentSlide(0)
          } else if (currentSlide >= newSlidesLength) {
            setCurrentSlide(newSlidesLength - 1)
          }
        },
        onError: () => {
          setClickedId(undefined)
        },
      })
    }
  }

  const handleReorder = () => {
    setReorderSlidesModal(true)
  }

  return (
    <>
      <div className="main-website-container">
        <h3 className="text-24 w-600">Partners</h3>
        <p className="text-16 w-400  text-gray">
          Manage partner sections shown on the website
        </p>

        <div className="section-counter-container">
          <div className="flex gap-20px align-center">
            <h4 className="text-20 w-600">
              Section {slides.length ? currentSlide + 1 : 0} of {slides.length}
            </h4>
            {slides.length > 0 && (
              <div className="flex gap-20px align-center">
                <ArrowLeft
                  size={20}
                  color="#2BA7CA"
                  className="pointer"
                  onClick={() =>
                    setCurrentSlide(
                      currentSlide === 0 ? slides.length - 1 : currentSlide - 1,
                    )
                  }
                />
                <ArrowRight
                  size={20}
                  color="#2BA7CA"
                  className="pointer"
                  onClick={() =>
                    setCurrentSlide(
                      currentSlide === slides.length - 1 ? 0 : currentSlide + 1,
                    )
                  }
                />
              </div>
            )}
          </div>
          <div className="btn-container">
            {slides.length > 0 &&
              (clickedId === slides[currentSlide]?.id && deleteLoading ? (
                <ButtonSpinner size={20} color="#2ba7ca" />
              ) : (
                <button
                  className="btn-delete"
                  onClick={() =>
                    handleDeleteSlide(slides[currentSlide].id as string)
                  }
                >
                  Delete Section
                </button>
              ))}
            <button className="bordered-prim-btn" onClick={handleReorder}>
              Reorder
            </button>
            <button
              className="bordered-prim-btn"
              onClick={() => partnerContentStore.getState().addSlide()}
            >
              Add Section
            </button>
          </div>
        </div>
        <div className="main-content-container">
          {isLoading || isFetching ? (
            <div className="w-full flex justify-center items-center py-40px">
              <Spinner />
            </div>
          ) : (
            <>
              <div className="editor-container">
                <PartnersEditor />
              </div>
              <div
                className="preview-container"
                style={{ backgroundColor: '#f6f6f3', borderRadius: '40px' }}
              >
                <PartnersContentPreview />
              </div>
            </>
          )}
        </div>
      </div>

      {reorderSlidesModal && <ReorderModal />}
    </>
  )
}

export default Partners
